import React, { useEffect, useRef, useState } from "react";
import { PILLARS } from "../mock";

function polar(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function annularSector(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const oS = polar(cx, cy, rOuter, startAngle);
  const oE = polar(cx, cy, rOuter, endAngle);
  const iE = polar(cx, cy, rInner, endAngle);
  const iS = polar(cx, cy, rInner, startAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${oS.x} ${oS.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${oE.x} ${oE.y}`,
    `L ${iE.x} ${iE.y}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${iS.x} ${iS.y}`,
    "Z",
  ].join(" ");
}

const useInView = (ref) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
};

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SystemWheel = ({ mode = "homepage", activePillars = null, startHereId = null }) => {
  const wrapRef = useRef(null);
  const inView = useInView(wrapRef);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Larger donut
  const W = 820;
  const H = 600;
  const cx = W / 2;
  const cy = H / 2;
  const rOuter = 205;
  const rInner = rOuter * 0.42;
  const gap = 2.0;

  const segments = PILLARS.map((p) => {
    const active = mode === "results" ? (activePillars || []).includes(p.id) : true;
    return { ...p, value: p.weight, active };
  });
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;

  let angle = 0;
  const arcs = segments.map((seg) => {
    const sweep = (seg.value / total) * 360;
    const start = angle + gap / 2;
    const end = angle + sweep - gap / 2;
    angle += sweep;
    const mid = (start + end) / 2;
    return { ...seg, start, end, mid, sweep };
  });

  const [counts, setCounts] = useState(() =>
    Object.fromEntries(
      PILLARS.map((p) => [
        p.id,
        prefersReducedMotion && mode === "results" ? p.weight : 0,
      ])
    )
  );
  useEffect(() => {
    if (mode !== "results" || !inView || prefersReducedMotion) return;
    const start = performance.now();
    const dur = 900;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setCounts(
        Object.fromEntries(PILLARS.map((p) => [p.id, Math.round(p.weight * eased)]))
      );
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, mode]);

  const rawVal = (seg) => (mode === "results" ? counts[seg.id] ?? 0 : seg.value);
  const startHereName = startHereId ? PILLARS.find((p) => p.id === startHereId)?.name : null;

  // Wrap long multi-word labels onto two stacked lines to shrink their footprint.
  const LABEL_FS = 22;
  const LINE_H = LABEL_FS + 3;
  const CHAR_W = LABEL_FS * 0.6;
  const LABEL_LINES = {
    cardiovascular: ["Cardiovascular", "Activity"],
    resistance: ["Muscle &", "Strength"],
    recovery: ["Rest &", "Recovery"],
  };
  const linesFor = (id, name) => LABEL_LINES[id] || [name];
  const labelWidth = (lines) => Math.max(...lines.map((l) => l.length)) * CHAR_W;
  const labelHeight = (lines) => lines.length * LINE_H;

  // Classify labels into top / bottom / left / right and de-collide using
  // each label's actual measured width/height so nothing overlaps.
  const buildLabels = () => {
    const active = arcs.filter((a) => a.value > 0);
    const items = active.map((a) => {
      const p0 = polar(cx, cy, rOuter, a.mid);
      const m = a.mid;
      let pos;
      if (m >= 338 || m < 22) pos = "top";
      else if (m >= 158 && m < 202) pos = "bottom";
      else pos = m < 180 ? "right" : "left";
      const lines = linesFor(a.id, a.name);
      return { a, p0, pos, x: p0.x, y: p0.y, lines, w: labelWidth(lines), h: labelHeight(lines) };
    });
    // vertical spacing for left/right based on stacked height
    ["left", "right"].forEach((side) => {
      const list = items.filter((it) => it.pos === side).sort((p, q) => p.y - q.y);
      for (let i = 1; i < list.length; i++) {
        const need = list[i - 1].h / 2 + list[i].h / 2 + 12;
        if (list[i].y - list[i - 1].y < need) list[i].y = list[i - 1].y + need;
      }
    });
    // horizontal spacing for top/bottom based on label width
    ["top", "bottom"].forEach((pos) => {
      const list = items.filter((it) => it.pos === pos).sort((p, q) => p.x - q.x);
      for (let i = 1; i < list.length; i++) {
        const need = list[i - 1].w / 2 + list[i].w / 2 + 18;
        if (list[i].x - list[i - 1].x < need) list[i].x = list[i - 1].x + need;
      }
    });
    return items;
  };
  const labels = buildLabels();

  const renderLabel = (it) => {
    const { a, p0, pos, lines } = it;
    const isActive = a.active;
    const labelFill = isActive ? "#fff" : "#6A6A6A";
    const leaderColor = isActive ? a.color : "#6A6A6A";
    let leaderPts;
    let tx;
    let ty;
    let anchor;
    if (pos === "right" || pos === "left") {
      const side = pos === "right" ? 1 : -1;
      const bendX = cx + side * (rOuter + 10);
      const endX = cx + side * (rOuter + 15);
      tx = cx + side * (rOuter + 19);
      ty = it.y + 7;
      anchor = side > 0 ? "start" : "end";
      leaderPts = `${p0.x},${p0.y} ${bendX},${it.y} ${endX},${it.y}`;
    } else if (pos === "top") {
      const topY = cy - rOuter - 20;
      tx = it.x;
      ty = topY;
      anchor = "middle";
      leaderPts = `${p0.x},${p0.y} ${p0.x},${topY + 12} ${it.x},${topY + 12}`;
    } else {
      const botY = cy + rOuter + 30;
      tx = it.x;
      ty = botY;
      anchor = "middle";
      leaderPts = `${p0.x},${p0.y} ${p0.x},${botY - 16} ${it.x},${botY - 16}`;
    }
    // For multi-line labels, offset the starting baseline so the block stays clear
    // of the wheel: top labels grow upward, everything else centers vertically.
    let startY = ty;
    if (lines.length > 1) {
      if (pos === "top") startY = ty - (lines.length - 1) * LINE_H;
      else if (pos === "left" || pos === "right") startY = ty - ((lines.length - 1) * LINE_H) / 2;
    }
    return (
      <g key={`lbl-${a.id}`}>
        <polyline points={leaderPts} fill="none" stroke={leaderColor} strokeWidth="1.6" opacity="0.9" />
        <circle cx={p0.x} cy={p0.y} r="3.2" fill={leaderColor} opacity="0.9" />
        <text
          x={tx}
          y={startY}
          textAnchor={anchor}
          className="font-display"
          style={{ fontSize: LABEL_FS, fontWeight: 900, fill: labelFill }}
        >
          {lines.map((ln, idx) => (
            <tspan key={idx} x={tx} dy={idx === 0 ? 0 : LINE_H}>
              {ln}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  const renderInSlicePct = (a) => {
    if (a.value <= 0) return null;
    const pt = polar(cx, cy, (rInner + rOuter) / 2, a.mid);
    const val = Math.round(rawVal(a));
    const fs = a.sweep < 20 ? 17 : 24;
    const pctFill = a.active ? "#14100a" : "#6A6A6A";
    return (
      <text
        key={`pct-${a.id}`}
        x={pt.x}
        y={pt.y + fs / 3}
        textAnchor="middle"
        className="font-display"
        style={{ fontSize: fs, fontWeight: 900, fill: pctFill }}
      >
        {`${val}%`}
      </text>
    );
  };

  return (
    <div ref={wrapRef} className="w-full flex flex-col items-center">
      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={{ overflow: "visible" }}
          role="img"
          aria-label={
            mode === "results"
              ? "Seven-system wheel. Active: " +
                (segments.filter((s) => s.active).map((s) => `${s.name} ${s.value}%`).join(", ") || "none") +
                ". Not active: " +
                (segments.filter((s) => !s.active).map((s) => `${s.name} ${s.value}%`).join(", ") || "none") +
                (startHereName ? `. Start here: ${startHereName}.` : ".")
              : "Seven systems: " + segments.map((s) => `${s.name} ${s.value}%`).join(", ")
          }
        >
          {arcs.map((a, i) => {
            if (a.value <= 0) return null;
            const isStartHere = a.id === startHereId;
            const offset = isStartHere ? polar(0, 0, 8, a.mid) : { x: 0, y: 0 };
            const dim = mode === "results" && !a.active;
            return (
              <path
                key={a.id}
                d={annularSector(cx, cy, rOuter, rInner, a.start, a.end)}
                fill={a.color}
                fillOpacity={dim ? 0.12 : 1}
                stroke={isStartHere ? "#ffffff" : "none"}
                strokeWidth={isStartHere ? 3 : 0}
                transform={`translate(${offset.x} ${offset.y})`}
                style={{
                  animationName: prefersReducedMotion || !inView ? "none" : "fadeUp",
                  animationDuration: "0.5s",
                  animationTimingFunction: "ease-out",
                  animationFillMode: "both",
                  animationDelay: `${i * 60}ms`,
                  opacity: inView || prefersReducedMotion ? 1 : 0,
                }}
              />
            );
          })}

          {labels.map((l) => renderLabel(l))}
          {arcs.map((a) => renderInSlicePct(a))}

          {mode === "results" && startHereName && (() => {
            const D = 2 * rOuter;
            const CENTER_LINES = {
              resistance: ["Muscle &", "Strength"],
              recovery: ["Rest &", "Recovery"],
              cardiovascular: ["Cardiovascular", "Activity"],
            };
            const clines = CENTER_LINES[startHereId] || [startHereName];
            const nameFs =
              startHereId === "cardiovascular"
                ? 0.045 * D
                : clines.length === 2
                ? 0.055 * D
                : 0.06 * D;
            const ebFs = 0.028 * D;
            const lineH = nameFs * 1.15;
            const eyebrowY = cy - 30;
            const firstLineY = clines.length === 2 ? cy - nameFs * 0.1 : cy + nameFs * 0.34;
            return (
              <>
                <text
                  x={cx}
                  y={eyebrowY}
                  textAnchor="middle"
                  className="font-display"
                  style={{ fontSize: ebFs, letterSpacing: ebFs * 0.18, fill: "rgba(255,255,255,0.55)", fontWeight: 700 }}
                >
                  START HERE
                </text>
                <text
                  x={cx}
                  y={firstLineY}
                  textAnchor="middle"
                  className="font-display"
                  style={{ fontSize: nameFs, fill: "#fff", fontWeight: 900 }}
                >
                  {clines.map((ln, idx) => (
                    <tspan key={idx} x={cx} dy={idx === 0 ? 0 : lineH}>
                      {ln}
                    </tspan>
                  ))}
                </text>
              </>
            );
          })()}
        </svg>
      </div>
    </div>
  );
};

export default SystemWheel;
